import { ReactComponent as AddIcon } from '$icons/Add.svg';
import { ReactComponent as CloseIcon } from '$icons/CloseGreen.svg';
import { ReactComponent as RightArrow } from '$icons/ArrowRight.svg';
import Button from '$components/Buttons/Button';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { useState } from 'react';
import SPKToast from '$components/SPKToast/SPKToast';
import { ReactComponent as Copy } from '$icons/copy.svg';
import copyToClipboard from '$utils/copyToClipboard';
import { Controller, useForm } from 'react-hook-form';

type VideoPromptFormType = {
  videoPrompt: string;
};

const VideoPrompt = () => {
  const [openPromptModal, setOpenPromptModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  const { control, handleSubmit, reset, formState } =
    useForm<VideoPromptFormType>({
      defaultValues: {
        videoPrompt: '',
      },
    });

  const closeModalHandler = () => {
    reset({ videoPrompt: '' });
    setOpenPromptModal(false);
    setGeneratedLink(null);
  };

  const generateLink = ({ videoPrompt }: VideoPromptFormType) => {
    const encodedPrompt = btoa(encodeURIComponent(videoPrompt));
    const next = encodeURIComponent(`/record-video?prompt=${encodedPrompt}`);
    const link = `${import.meta.env.REACT_APP_SPK_URL}/auth/signin?next=${next}`;
    setGeneratedLink(link);
  };

  return (
    <>
      <div className="generatePrompt">
        <Button
          bgColor="#006789"
          showText
          text="Generate Prompt"
          icon={AddIcon}
          invertTextContent
          styling="px-4 whitespace-nowrap"
          onClick={() => {
            setOpenPromptModal((prev) => !prev);
            setGeneratedLink(null);
          }}
        />
      </div>
      <ModalInTailwind isOpen={openPromptModal} closeModal={closeModalHandler}>
        <div className="flex p-8 max-h-screen overflow-hidden justify-center w-full">
          <div className="flex flex-col bg-white p-8 w-3/5 max-h-full">
            <header className="flex flex-row justify-between items-center mb-8 w-full">
              <div className="text-xl font-bold whitespace-nowrap">
                Generate a Video Prompt
              </div>
              <button
                aria-label="Close"
                type="button"
                onClick={closeModalHandler}
              >
                <CloseIcon />
              </button>
            </header>
            {!generatedLink ? (
              <form onSubmit={handleSubmit(generateLink)}>
                <div className="flex flex-col w-full gap-4">
                  <h3 className="text-lg font-semibold">
                    Write the Candidate Prompt
                  </h3>
                  <Controller
                    control={control}
                    name="videoPrompt"
                    rules={{ required: 'This field is required' }}
                    render={({ field, fieldState }) => (
                      <div>
                        <textarea
                          {...field}
                          data-testid="video-prompt-textarea"
                          className="border rounded w-full min-h-40 p-2"
                          placeholder={`Type your prompt for the Video you want the candidates to answer here. This will create a link you can share with candidates via Whatsapp or email.\n\nWhatever you write here will be what the candidates see.`}
                        />
                        {fieldState.error?.message && (
                          <div className="text-spk-brightRed text-xs">
                            {fieldState.error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                  <div className="flex flex-row mt-4 self-end w-60">
                    <Button
                      text="Cancel"
                      showText
                      textStyles={{ color: '#079e88' }}
                      onClick={closeModalHandler}
                    />
                    <Button
                      text="Generate"
                      icon={RightArrow}
                      invertTextContent
                      showText
                      bgColor="#079e88"
                      disabled={!formState.isValid}
                      customStyles={!formState.isValid && { opacity: '.25' }}
                      submit
                      data-testid="generateTokens"
                    />
                  </div>
                </div>
              </form>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-600">
                  Share this link with candidates. They sign in with their email
                  and the magic link takes them directly to record their video.
                </p>
                <div className="flex flex-row items-center gap-2 border rounded p-3 bg-gray-50">
                  <span className="break-all text-sm flex-1">
                    {generatedLink}
                  </span>
                  <button
                    type="button"
                    aria-label="Copy link"
                    onClick={async () => {
                      await copyToClipboard(generatedLink);
                      SPKToast('Link copied to clipboard');
                    }}
                  >
                    <Copy />
                  </button>
                </div>
                <div className="self-end mt-4">
                  <Button
                    text="Close"
                    showText
                    textStyles={{ color: '#079e88' }}
                    onClick={closeModalHandler}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalInTailwind>
    </>
  );
};
export default VideoPrompt;
